import { Trans } from 'react-i18next'

import {
  Button,
  Flex,
  IconWarningCircleFill,
  Modal,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  Spinner,
  Text
} from '@vtex/shoreline'

import { useAPPTranslation } from '~/i18n/useAPPTranslation'

type UnlinkModalProps = {
  insertionOrder: CampaignLinkedInsertionOrder | null
  isUnlinking: boolean
  onConfirm: () => void
  onClose: () => void
}

export const CampaignInsertionOrderUnlinkModal = ({
  insertionOrder,
  isUnlinking,
  onConfirm,
  onClose
}: UnlinkModalProps) => {
  const { t } = useAPPTranslation()

  const handleClose = () => {
    if (isUnlinking) return
    onClose()
  }

  return (
    <Modal
      size="medium"
      open={!!insertionOrder}
      onClose={handleClose}
      data-testid="campaign-io-unlink-modal"
    >
      <ModalHeader>
        <Flex align="center" gap="$space-3">
          <IconWarningCircleFill style={{ color: 'var(--sl-fg-critical)' }} />
          <ModalHeading
            style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}
          >
            {t('rm:campaign.detail.insertionOrder.unlinkModal.title')}
          </ModalHeading>
        </Flex>
        <ModalDismiss size="large" disabled={isUnlinking} />
      </ModalHeader>
      <ModalContent>
        <Text as="p" data-testid="campaign-io-unlink-modal-body">
          <Trans
            i18nKey="rm:campaign.detail.insertionOrder.unlinkModal.body"
            values={{ name: insertionOrder?.name }}
            components={{ strong: <strong /> }}
          />
        </Text>
      </ModalContent>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isUnlinking}
          data-testid="campaign-io-unlink-cancel"
        >
          {t('c:actions.cancel')}
        </Button>
        <Button
          variant="critical"
          onClick={onConfirm}
          disabled={isUnlinking}
          aria-busy={isUnlinking}
          data-testid="campaign-io-unlink-confirm"
        >
          {isUnlinking ? (
            <>
              <Spinner size={16} />
              {t('rm:campaign.detail.insertionOrder.actions.unlinking')}
            </>
          ) : (
            t('rm:campaign.detail.insertionOrder.actions.unlink')
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
